import { useEffect, useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Laptop, Camera, BarChart3, PlayCircle, Heart } from "lucide-react";

/**
 * Floating doodle icons (laptop / camera / chart / play / heart).
 * Same animation pattern as before — only the icon graphics changed.
 */

const ICON_SET = [Laptop, Camera, BarChart3, PlayCircle, Heart];

const POSITIONS = [
    { top: "10%", left: "4%",  size: 40, drift: 18, rot: -14, depth: 0.9, delay: 0 },
    { top: "18%", left: "90%", size: 34, drift: 14, rot: 22,  depth: 0.7, delay: 0.4 },
    { top: "76%", left: "7%",  size: 30, drift: 12, rot: 0,   depth: 0.5, delay: 0.8 },
    { top: "70%", left: "92%", size: 36, drift: 16, rot: -8,  depth: 0.8, delay: 1.2 },
    { top: "48%", left: "2%",  size: 38, drift: 14, rot: 0,   depth: 0.6, delay: 0.6 },
    { top: "30%", left: "46%", size: 28, drift: 18, rot: 0,   depth: 0.4, delay: 1.0 },
    { top: "85%", left: "52%", size: 32, drift: 12, rot: 12,  depth: 0.7, delay: 1.4 },
    { top: "12%", left: "62%", size: 26, drift: 14, rot: 0,   depth: 0.6, delay: 1.6 },
    { top: "58%", left: "76%", size: 32, drift: 16, rot: 0,   depth: 0.5, delay: 1.8 },
    { top: "42%", left: "20%", size: 26, drift: 12, rot: -16, depth: 0.8, delay: 2.0 },
    { top: "92%", left: "30%", size: 30, drift: 12, rot: 0,   depth: 0.6, delay: 2.2 },
    { top: "22%", left: "30%", size: 32, drift: 14, rot: -10, depth: 0.5, delay: 2.4 },
];

const ICONS = POSITIONS.map((p, i) => ({ ...p, Icon: ICON_SET[i % ICON_SET.length] }));

function MagneticIcon({ item, mouseX, mouseY, tone }) {
    const { Icon, top, left, size, drift, rot, depth, delay } = item;
    const color = tone === "dark" ? "rgba(255,255,255,0.4)" : "rgba(15,61,122,0.32)";

    const px = useSpring(useTransform(mouseX, (v) => v * depth * 30), {
        stiffness: 60, damping: 18, mass: 0.6,
    });
    const py = useSpring(useTransform(mouseY, (v) => v * depth * 30), {
        stiffness: 60, damping: 18, mass: 0.6,
    });

    return (
        <motion.span
            style={{ top, left, color, x: px, y: py }}
            className="absolute inline-flex"
        >
            <motion.span
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{
                    opacity: 1,
                    scale: 1,
                    y: [0, -drift, 0],
                    rotate: [rot, rot + 10, rot],
                }}
                transition={{
                    opacity: { duration: 1, delay },
                    scale: { duration: 1, delay },
                    y: { duration: 6 + (size % 4), repeat: Infinity, ease: "easeInOut", delay },
                    rotate: { duration: 10 + (size % 5), repeat: Infinity, ease: "easeInOut", delay },
                }}
                className="inline-flex"
            >
                <Icon style={{ width: size, height: size }} strokeWidth={1.4} />
            </motion.span>
        </motion.span>
    );
}

export default function FloatingIcons({ tone = "light" }) {
    const containerRef = useRef(null);
    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const onMove = (e) => {
            const r = el.getBoundingClientRect();
            const cx = r.left + r.width / 2;
            const cy = r.top + r.height / 2;
            const nx = Math.max(-1, Math.min(1, (e.clientX - cx) / (r.width / 2)));
            const ny = Math.max(-1, Math.min(1, (e.clientY - cy) / (r.height / 2)));
            mouseX.set(nx);
            mouseY.set(ny);
        };
        const onLeave = () => { mouseX.set(0); mouseY.set(0); };
        window.addEventListener("mousemove", onMove, { passive: true });
        window.addEventListener("mouseleave", onLeave);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseleave", onLeave);
        };
    }, [mouseX, mouseY]);

    return (
        <div
            ref={containerRef}
            aria-hidden
            className="pointer-events-none absolute inset-0 overflow-hidden z-[2]"
        >
            {ICONS.map((it, i) => (
                <MagneticIcon key={i} item={it} mouseX={mouseX} mouseY={mouseY} tone={tone} />
            ))}
        </div>
    );
}
